import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { AccountView, AffiliateSummary } from '@/types/api';

/**
 * Matches the server's `UpdateAccountDto` — both fields are optional so
 * callers can send a partial patch (e.g. only `firstName` from the onboarding
 * step). Empty strings are rejected server-side, so the UI should never send
 * whitespace-only values.
 */
export interface UpdateAccountPayload {
  firstName?: string;
  lastName?: string;
  /**
   * Parental-control toggle. Mirrors `accounts.comments_enabled` —
   * flipping false hides book comments for the family's readers and
   * blocks them from posting new ones. See
   * `ReviewsService.assertCommentsAllowed` for the server enforcement.
   */
  commentsEnabled?: boolean;
  /** Email-notification preferences (Account → "Email Notifications"). */
  marketingEmailsEnabled?: boolean;
  accountNotificationsEnabled?: boolean;
}

export const accountsService = {
  getMe() {
    return apiRequest<AccountView>(apiRoutes.accounts.me);
  },
  updateMe(payload: UpdateAccountPayload) {
    return apiRequest<AccountView>(apiRoutes.accounts.me, {
      method: 'PATCH',
      body: payload,
    });
  },

  /**
   * Permanently delete the current account and all its data. Resolves on 204;
   * throws an ApiError with status 409 when a renewing subscription is still
   * active (the caller gates the button on this, but the server enforces it).
   */
  deleteMe() {
    return apiRequest<void>(apiRoutes.accounts.me, { method: 'DELETE' });
  },

  /**
   * Set or replace the family's parent PIN. Server hashes; we never
   * cache the raw PIN client-side. Returns the refreshed AccountView
   * so the caller can flip `hasParentPin` true without a follow-up GET.
   */
  setParentPin(pin: string) {
    return apiRequest<AccountView>(apiRoutes.accounts.parentPin, {
      method: 'PUT',
      body: { pin },
    });
  },

  /** Remove the family's parent PIN. */
  clearParentPin() {
    return apiRequest<AccountView>(apiRoutes.accounts.parentPin, {
      method: 'DELETE',
    });
  },

  /**
   * Verify a candidate PIN. Resolves with `undefined` on success (204);
   * throws an ApiError on mismatch (401), missing PIN (404), or any
   * other failure. Callers map the 401 to a "wrong PIN" message.
   */
  verifyParentPin(pin: string) {
    return apiRequest<void>(apiRoutes.accounts.verifyParentPin, {
      method: 'POST',
      body: { pin },
    });
  },

  /**
   * Affiliate dashboard payload. The code is stable per-account; the
   * `monthlySignups` list only contains months with non-zero counts —
   * the UI pads in a zero entry for the current month locally.
   */
  getAffiliate() {
    return apiRequest<AffiliateSummary>(apiRoutes.accounts.affiliate);
  },

  /**
   * Submit a "Request to Become an Affiliate" application. Returns the
   * refreshed AccountView (with `affiliateStatus: 'pending'`) so the
   * caller can update the store without a follow-up GET. Server enforces
   * paid-only eligibility and rejects duplicate requests.
   */
  requestAffiliate(payload: RequestAffiliatePayload) {
    return apiRequest<AccountView>(apiRoutes.accounts.affiliateRequest, {
      method: 'POST',
      body: payload,
    });
  },

  /**
   * Claim or replace the vanity affiliate slug. Returns the refreshed
   * `AffiliateSummary` (with the new `slug`) so the caller can re-render the
   * custom link without a follow-up GET. Rejects with a 409 when the slug is
   * taken and a 400 when it's malformed — the caller surfaces the message.
   */
  setAffiliateSlug(slug: string) {
    return apiRequest<AffiliateSummary>(apiRoutes.accounts.affiliateSlug, {
      method: 'PUT',
      body: { slug },
    });
  },

  /**
   * Release the vanity slug, reverting the share link to the permanent hex
   * code. Returns the refreshed `AffiliateSummary` (with `slug: null`).
   */
  clearAffiliateSlug() {
    return apiRequest<AffiliateSummary>(apiRoutes.accounts.affiliateSlug, {
      method: 'DELETE',
    });
  },
};

/** Matches the server's `RequestAffiliateDto`. */
export interface RequestAffiliatePayload {
  firstName: string;
  lastName: string;
  /** Website or social-media account. */
  contactUrl: string;
  contactEmail: string;
}
